import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UsersFour, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

export default function ClientGroups() {
  const [clients, setClients] = useState(null);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([])); }, []);

  if (!clients) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading client groups…</div>;

  const groups = {};
  clients.forEach((c) => {
    const key = (c.group_name || "").trim();
    if (!key) return;
    (groups[key] = groups[key] || []).push(c);
  });
  const entries = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  const ungrouped = clients.filter((c) => !(c.group_name || "").trim());

  return (
    <div className="space-y-6" data-testid="client-groups-page">
      <PageHeader overline="Clients" title="Client Groups" subtitle="Related or family entities grouped under one umbrella — handy for group loans and co-owned properties" />

      {entries.length === 0 ? (
        <EmptyState icon={UsersFour}>No groups yet. Open any client profile → Tags &amp; Group and set a group name to bundle related borrowers.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {entries.map(([name, members]) => (
            <Card key={name} className="border-border overflow-hidden" data-testid={`group-${name.replace(/\W+/g, "-")}`}>
              <div className="flex items-center gap-2 px-5 py-3 bg-primary text-white">
                <UsersFour size={18} weight="fill" className="text-accent" />
                <span className="font-display font-semibold truncate">{name}</span>
                <span className="ml-auto text-[11px] text-white/60">{members.length} members</span>
              </div>
              <div className="divide-y divide-border">
                {members.map((c) => (
                  <Link key={c.id} to={`/app/clients/${c.id}`} className="flex items-center justify-between gap-2 px-5 py-3 hover:bg-muted/50 transition-colors duration-150" data-testid={`group-member-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-primary font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.client_type}{c.phone ? ` · ${c.phone}` : ""}</div>
                    </div>
                    <CaretRight size={14} className="text-muted-foreground shrink-0" />
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {ungrouped.length > 0 && (
        <Card className="p-5 border-border">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Ungrouped ({ungrouped.length})</div>
          <div className="flex flex-wrap gap-2">
            {ungrouped.map((c) => (
              <Link key={c.id} to={`/app/clients/${c.id}`} className="px-3 py-1.5 rounded-full text-xs border border-border bg-muted text-primary hover:border-accent transition-colors duration-150" data-testid={`ungrouped-${c.id}`}>
                {c.name}
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
